/* eslint-disable no-param-reassign */
// External
import {
  getBlob,
  uploadString,
  deleteObject,
  getDownloadURL,
  ref as storageRef,
} from 'firebase/storage'
import {
  doc,
  where,
  query,
  getDoc,
  setDoc,
  getDocs,
  updateDoc,
  collection,
  onSnapshot,
  getCountFromServer,
} from 'firebase/firestore'
import { getUnix } from '@/libs/date-format'
import html2canvas from 'html2canvas'
import { RevisionEventType } from '@core/revisions/revisions'

// Local
import store from '@/store'
import i18n from '@/libs/i18n'
import { allowed, FEATURES } from '@/auth'
import {
  constructQuery,
  updateIframeElement,
  retrieveThemeExternalResources as retrieveExternalResources,
} from '@core/utils/utils'

import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'

export default {
  namespaced: true,
  state: {
    count: 0,
    current: {
      data: {},
    },
    currentFiles: {},
    activeTheme: null,
    newThemeFolder: {
      form: {},
      parentNode: {
        children: [],
      },
    },
    tenantThemesList: [],
    sharedThemesList: [],
    currentComponents: [],
    externalResources: [],
    uiComponentThumbnails: [],
    thumbnailCanvasElement: null,
    lastActiveThemeEditTab: localStorage.getItem('novti-last-active-theme-edit-tab') || 0,
  },
  getters: {
    getCurrent: state => state.current,
    getCount: state => state.count,
    getActiveTheme: state => state.activeTheme,
    getCurrentFiles: state => state.currentFiles,
    getNewThemeFolder: state => state.newThemeFolder,
    getTenantThemes: state => state.tenantThemesList,
    getSharedThemes: state => state.sharedThemesList,
    getCurrentComponents: state => state.currentComponents,
    getUIComponentThumbnails: state => state.uiComponentThumbnails,
    getThumbnailCanvasElement: state => state.thumbnailCanvasElement,
    getLastActiveThemeEditTab: state => state.lastActiveThemeEditTab,
    getExternalResources: state => type => state.externalResources.filter(item => item.type === type),
    getActiveExternalResources: state => type => state.activeTheme?.externalResources.filter(item => item.type === type),
  },
  mutations: {
    SET_CURRENT_FILES(state, payload) {
      state.currentFiles = payload
    },
    SET_ACTIVE_THEME(state, payload) {
      state.activeTheme = payload ?? {}
    },
    SET_CURRENT_COMPONENTS(state, payload) {
      state.currentComponents = payload ?? []
    },
    SET_THUMBNAIL_CANVAS_ELEMENT(state, payload) {
      state.thumbnailCanvasElement = payload
    },
    SET_CURRENT(state, payload) {
      state.current = {
        snapshot: payload,
        data: { ...payload.data(), id: payload.id },
      }
    },
    SET_CURRENT_EXTERNAL_RESOURCES(state, payload) {
      state.externalResources = payload
    },
    UPDATE_TENANT_THEMES_LIST(state, payload) {
      state.tenantThemesList = payload
    },
    UPDATE_SHARED_THEMES_LIST(state, payload) {
      state.sharedThemesList = payload
    },
    SET_UI_COMPONENT_THUMBNAILS(state, payload) {
      state.uiComponentThumbnails = payload
    },
    DESTROY_CURRENT(state) {
      state.current = {}
    },
    SET_LAST_ACTIVE_THEME_EDIT_TAB(state, payload) {
      localStorage.setItem('novti-last-active-theme-edit-tab', payload)

      state.lastActiveThemeEditTab = payload
    },
    SET_NEW_THEME_FOLDER(state, payload) {
      state.newThemeFolder = payload
    },
    SET_COUNT(state, payload) {
      state.count = payload
    },
  },
  actions: {
    /**
     * Creates a document by the given payload.
     *
     * @param {Object} obj
     * @param {Object} payload
     *
     * @returns {Promise}
     */
    create({}, payload) {
      return new Promise(async (resolve, reject) => {
        if (!allowed(FEATURES.THEMES_QUOTA)) {
          reject()

          store.dispatch('notify', {
            body: i18n.t('You have reached your {title} limit', { title: i18n.t('Themes') }),
            variant: 'danger',
          })

          return
        }

        const { themes } = tenantCtx()
        const { themesStorage } = tenantCtx()

        const docRef = await doc(themes)

        // This is required if we want to filter on those fields, as per Firestore query limitations
        // eslint-disable-next-line no-param-reassign
        payload.id = docRef.id
        payload.active = false
        payload.deletedAt = null
        payload.creating = true
        payload.compiling = false
        payload.createdAt = getUnix()
        payload.updatedAt = getUnix()
        payload.storage = `${themesStorage.fullPath}/${docRef.id}`

        setDoc(docRef, payload)
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.CREATE_THEMES,
              id: docRef.id,
              newValue: payload,
              previousValue: {},
            })

            store.dispatch('notify', {
              title: i18n.t('Great!'),
              body: i18n.t('A new {title} has been created 🚀', {
                title: i18n.t('Theme'),
              }),
            })

            resolve(docRef)
          })
          .catch(error => {
            console.debug(error)

            store.dispatch('notify', {
              body: i18n.t('Something went wrong creating the Theme'),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches the shared Themes.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {Object} obj.dispatch
     *
     * @returns {Promise}
     */
    fetchSharedThemes({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        const { sharedThemes } = tenantCtx()

        const q = constructQuery(sharedThemes, 'createdAt', {
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(async querySnapshot => {
            const themes = await Promise.all(
              querySnapshot.docs.map(async docRef => ({
                id: docRef.id,
                _themeThumbnails: await dispatch('fetchThemeThumbnails', { id: docRef.id, target: 'shared' }),
                ...docRef.data(),
              })),
            )

            commit('UPDATE_SHARED_THEMES_LIST', themes)
            resolve(themes)
          })
          .catch(error => {
            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('Novti Themes') }),
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches the tenant's Themes.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {Object} obj.dispatch
     *
     * @returns {Promise}
     */
    fetchTenantThemes({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()

        const q = constructQuery(themes, 'updatedAt', {
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(async querySnapshot => {
            const tenantThemes = await Promise.all(
              querySnapshot.docs.map(async docRef => ({
                id: docRef.id,
                _themeThumbnails: await dispatch('fetchThemeThumbnails', { id: docRef.id }),
                ...docRef.data(),
              })),
            )

            commit('UPDATE_TENANT_THEMES_LIST', tenantThemes)
            resolve(tenantThemes)
          })
          .catch(error => {
            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('tenant Themes') }),
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches the active Theme.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {Object} obj.dispatch
     *
     * @returns {Promise} - Returns error if the document doesn't exist.
     */
    fetchActiveTheme({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const q = query(themes, where('active', '==', true))

        getDocs(q)
          .then(async docSnapshot => {
            const [docRef] = docSnapshot.docs

            if (docRef === undefined) {
              resolve(null)

              return
            }

            // Fetch related resources
            const activeThemeFiles = await dispatch('fetchCurrentFiles', docRef?.id)
            const activeThemeExternalResources = await dispatch('fetchCurrentExternalResources', docRef?.id)

            const activeTheme = {
              data: docRef?.data(),
              files: activeThemeFiles,
              externalResources: activeThemeExternalResources,
            }

            commit('SET_ACTIVE_THEME', activeTheme)

            resolve(activeTheme)
          })
          .catch(error => {
            reject(error)

            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('Active Theme') }),
            })
          })
      })
    },

    /**
     * Fetches document count
     *
     * @param {Object} context - Vuex context
     *
     * @returns {Promise<number>} - Count of matching documents
     */
    fetchCount({ commit }) {
      return new Promise(async (resolve, reject) => {
        try {
          const { themes } = tenantCtx()

          const q = query(
            themes,
            where('deletedAt', '==', null),
          )

          const snapshot = await getCountFromServer(q)
          const { count } = snapshot.data()

          commit('SET_COUNT', count)
          resolve(count)
        } catch (error) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('themes') }),
            variant: 'danger',
          })
          reject(error)
        }
      })
    },

    /**
     * Fetches a Theme by the given ID.
     *
     * @param {Object} obj
     * @param {string} id The document ID
     *
     * @returns {Promise}
     */
    fetchById({}, id) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const docRef = doc(themes, id)

        getDoc(docRef)
          .then(docSnapshot => {
            if (!docSnapshot.exists() || docSnapshot.get('deletedAt') !== null) {
              reject(new Error('404'))
            }

            resolve(docSnapshot)
          })
          .catch(error => {
            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the Theme'),
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches the current Theme by the given ID.
     *
     * @param {Object} obj
     * @param {Object} obj.dispatch
     * @param {Object} obj.commit
     * @param {string} id - The current Theme ID
     *
     * @returns {Promise}
     */
    fetchCurrent({ dispatch, commit }, id) {
      return new Promise((resolve, reject) => {
        dispatch('fetchById', id)
          .then(async res => {
            // Fetch related resources
            const currentFiles = await dispatch('fetchCurrentFiles', id)
            const currentExternalResources = await dispatch('fetchCurrentExternalResources', id)

            commit('SET_CURRENT', res)
            commit('SET_CURRENT_FILES', currentFiles)
            commit('SET_CURRENT_EXTERNAL_RESOURCES', currentExternalResources)
            resolve(res)
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    /**
     * Fetches the files of the current Theme by the given ID.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {string} id - The current Theme ID
     *
     * @returns {Promise}
     */
    fetchCurrentFiles({ commit }, id) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const filesCollection = collection(themes, id, 'files')

        const q = constructQuery(filesCollection, 'name', {
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(docSnapshot => {
            const files = docSnapshot.docs
              .map(docRef => ({
                id: docRef.id,
                ...docRef.data(),
              }))

            commit('SET_CURRENT_FILES', files)
            resolve(files)
          })
          .catch(error => {
            reject(error)

            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', { title: i18n.t('Theme\'s files') }),
            })
          })
      })
    },

    /**
     * Fetches the thumbnails of a Theme by the given ID and target collection and bucket.
     *
     * @param {Object} obj
     * @param {Object} payload.id - The Theme ID
     * @param {Object} payload.target - The target collection and bucket
     *
     * @returns {Promise}
     */
    fetchThemeThumbnails({ }, { id, target = 'specific' }) {
      return new Promise((resolve, reject) => {
        // Get the tenant-specific and shared Themes collection
        const { themes, sharedThemes } = tenantCtx()

        // Get the tenant-specific and client themes root buckets
        const { themesStorage, sharedThemesStorage } = tenantCtx()

        const collections = {
          specific: themes,
          shared: sharedThemes,
        }

        const buckets = {
          specific: themesStorage,
          shared: sharedThemesStorage,
        }

        const filesCollection = collection(collections[target], id, 'files')

        const q = constructQuery(filesCollection, 'name', {
          where: [
            where('deletedAt', '==', null),
            where('type', '==', 'html'),
          ],
        })

        getDocs(q)
          .then(docSnapshot => {
            const files = docSnapshot.docs
            const thumbnails = []
            const devices = ['mobile', 'tablet', 'desktop']

            files.forEach(async file => {
              const thumbnailUrls = {}
              const fileData = file.data()

              devices.forEach(async device => {
                const fileThumbnailRef = storageRef(
                  buckets[target],
                  `${id}/${fileData.path}/images/${fileData.name}.${fileData.type}.${device}.png`,
                )

                try {
                  thumbnailUrls[device] = await getDownloadURL(fileThumbnailRef)
                } catch (error) {
                  console.debug(error)

                  thumbnailUrls[device] = store.getters['pages/getThumbnailPlaceholder']
                }
              })

              thumbnails.push(thumbnailUrls)
            })

            resolve(thumbnails)
          })
          .catch(error => {
            console.error(error)

            reject(error)
          })
      })
    },

    /**
     * Fetches the thumbnails of a UI Component by the given Theme ID and target bucket.
     *
     * @param {Object} obj
     * @param {Object} payload.themeId - The Theme ID
     * @param {Object} payload.uiComponent - The UI Component
     * @param {Object} payload.target - The target bucket
     *
     * @returns {Promise}
     */
    fetchUIComponentThumbnails({ getters }, { themeId, uiComponent, target = 'specific' }) {
      return new Promise(resolve => {
        const { themesStorage, sharedThemesStorage } = tenantCtx()

        const buckets = {
          specific: themesStorage,
          shared: sharedThemesStorage,
        }

        const thumbnailUrls = {}
        const devices = ['mobile', 'tablet', 'desktop']

        devices.forEach(async device => {
          const fileThumbnailRef = storageRef(
            buckets[target],
            `${themeId}/${uiComponent.path}/images/${uiComponent.name}.${uiComponent.type}.${device}.png`,
          )

          try {
            thumbnailUrls[device] = await getDownloadURL(fileThumbnailRef)
          } catch (error) {
            console.debug(error)

            thumbnailUrls[device] = store.getters['pages/getThumbnailPlaceholder']
          }
        })

        resolve(thumbnailUrls)
      })
    },

    /**
     * Fetches the current Theme's External Resources.
     *
     * @param {Object} obj
     * @param {Object} obj.commit
     * @param {string} id - The current Theme ID
     *
     * @returns {Promise}
     */
    fetchCurrentExternalResources({ commit }, id) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const externalResourcesCollection = collection(themes, id, 'externalResources')

        const q = constructQuery(externalResourcesCollection, 'order', {
          where: [where('deletedAt', '==', null)],
        })

        getDocs(q)
          .then(docSnapshot => {
            const externalResources = docSnapshot.docs
              .map(docRef => ({
                id: docRef.id,
                ...docRef.data(),
              }))

            commit('SET_CURRENT_EXTERNAL_RESOURCES', externalResources)
            resolve(externalResources)
          })
          .catch(error => {
            reject(error)

            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', {
                title: i18n.t('Theme\'s External Resources'),
              }),
            })
          })
      })
    },

    /**
     * Fetches a file of the current Theme.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to fetch the file
     *
     * @returns {Promise}
     */
    fetchCurrentThemeFile({ state }, payload) {
      return new Promise(async (resolve, reject) => {
        const { themesStorage } = tenantCtx()
        const fileRef = storageRef(
          themesStorage,
          `${state.current.data.id}/${payload.path}/${payload.name}.${payload.type}`,
        )

        getBlob(fileRef)
          .then(fileBlob => {
            resolve(fileBlob)
          })
          .catch(error => {
            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', {
                title: i18n.t(`the content of ${payload.name}.${payload.type}`),
              }),
            })

            reject(error)
          })
      })
    },

    /**
     * Fetches a file of the active Theme.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to fetch the file
     *
     * @returns {Promise}
     */
    fetchActiveThemeFile({ state }, payload) {
      return new Promise(async (resolve, reject) => {
        const { themesStorage } = tenantCtx()
        const fileRef = storageRef(
          themesStorage,
          `${state.activeTheme.data.id}/${payload.path}/${payload.name}.${payload.type}`,
        )

        getBlob(fileRef)
          .then(fileBlob => {
            resolve(fileBlob)
          })
          .catch(error => {
            store.dispatch('notify', {
              variant: 'danger',
              body: i18n.t('Something went wrong retrieving the {title}', {
                title: i18n.t(`the content of ${payload.name}.${payload.type}`),
              }),
            })

            reject(error)
          })
      })
    },

    /**
     * Updates the current Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} obj.dispatch
     * @param {Object} payload - The payload used to update the Theme
     *
     * @returns {Promise}
     */
    updateCurrent({ state, dispatch }, payload) {
      return new Promise(async (resolve, reject) => {
        try {
          const previousValue = state.current.data
          const { id } = state.current.snapshot

          const value = {
            ...previousValue,
            ...payload,
            updatedAt: getUnix(),
          }

          dispatch('update', {
            value,
            previousValue,
          })

          dispatch('fetchCurrent', value.id) // Update the current data as well

          resolve(id)
        } catch (error) {
          console.debug(error)

          reject(error)
        }
      })
    },

    /**
     * Updates a Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} payload.value - The new value
     * @param {Object} payload.previousValue - The previous value
     *
     * @returns {Promise}
     */
    update({}, { value, previousValue }) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const docRef = doc(themes, value.id)

        updateDoc(docRef, value)
          .then(() => {
            store.dispatch('revisions/create', {
              id: value.id,
              previousValue,
              newValue: { ...value },
              event: RevisionEventType.UPDATE_THEMES,
            })

            resolve(true)
          })
          .catch(error => {
            store.dispatch('notify', {
              body: i18n.t('Something went wrong updating the {title}', { title: i18n.t('Theme') }),
              variant: 'danger',
            })

            reject(error)
          })
      })
    },

    /**
     * Updates the current Theme file by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} obj.getters
     * @param {Object} payload - The payload used to update the file
     *
     * @returns {Promise}
     */
    updateCurrentThemeFile({ state, getters }, payload) {
      return new Promise(async (resolve, reject) => {
        try {
          const { themes, themesStorage } = tenantCtx()
          const docRef = doc(themes, `${state.current.data.id}/files/${payload.id}`)

          // Let us see if we have to upload something
          if (payload.newValue !== payload.oldValue) {
            const file = `${state.current.data.id}/${payload.path}/${payload.name}.${payload.type}`
            const fileRef = storageRef(
              themesStorage,
              file,
            )

            await uploadString(fileRef, payload.newValue)

            // Since we are already on the client side, we can easily take screenshots
            // of UI component elements to update the thumbnails. Doing this server-side is hard and inefficient.
            if (payload.type === 'html') {
              const current = getters.getCurrent
              const currentStyles = getters.getExternalResources('css')
              const currentScripts = getters.getExternalResources('js')
              const externalResources = retrieveExternalResources(current, currentStyles, currentScripts)

              const iframeDoc = updateIframeElement(payload.newValue, state.thumbnailCanvasElement, externalResources)
              const iframeParentElement = document.getElementById('thumbnailCanvasContainer')
              const deviceWidths = {
                mobile: 'w-50',
                tablet: 'w-75',
                desktop: 'w-100',
              }

              // Generates a canvas for each device by dynamically mimicking the device width
              const processDevice = async (device, width) => {
                iframeParentElement.classList.add(width) // Set the width class dynamically

                const thumbnailStoragePath = `${state.current.data.id}/${payload.path}/images/${payload.name}.${payload.type}.${device}.png`
                const canvas = await html2canvas(iframeDoc.body)
                const fileThumbnailRef = storageRef(themesStorage, thumbnailStoragePath)
                const thumbnailDataUrl = canvas.toDataURL('image/png', 1)

                iframeParentElement.classList.remove(width)

                await uploadString(fileThumbnailRef, thumbnailDataUrl, 'data_url')
              }

              // Iterate over the deviceWidths and process each device
              const processPromises = Object.entries(deviceWidths).map(([device, width]) => processDevice(device, width))

              await Promise.all(processPromises)
            }

            // Sync the values for the participating units
            // and prevent unnecessary Storage updates
            payload.oldValue = payload.newValue
          }

          // Prevent unnecessary Firestore updates
          delete payload.changed

          const { newValue, oldValue, ...fileData } = payload

          await updateDoc(docRef, {
            ...fileData,
            updatedAt: getUnix(),
          })

          resolve(true)
        } catch (error) {
          console.debug(error)

          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong updating the {title}', {
              title: i18n.t(`file ${payload.name}.${payload.type}`),
            }),
          })

          reject(error)
        }
      })
    },

    /**
     * Updates or inserts an External Resource of the current Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to update the External Resource
     *
     * @returns {Promise}
     */
    upsertCurrentThemeExternalResource({ state }, payload) {
      return new Promise(async (resolve, reject) => {
        try {
          const { themes } = tenantCtx()
          const externalResources = collection(themes, state.current.data.id, 'externalResources')

          if (!payload.id) {
            const docRef = doc(externalResources)

            setDoc(docRef, { ...payload }, { merge: true })
              .then(() => resolve(docRef))
              .catch(error => reject(error))
          } else {
            const docRef = doc(externalResources, payload.id)

            await updateDoc(docRef, {
              ...payload,
              updatedAt: getUnix(),
            })
          }

          resolve(true)
        } catch (error) {
          console.debug(error)

          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong updating the {title}', {
              title: i18n.t(`External Resource ${payload.name}`),
            }),
          })

          reject(error)
        }
      })
    },

    /**
     * Deletes a file of the current Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to delete the file
     *
     * @returns {Promise}
     */
    deleteCurrentThemeFile({ state }, payload) {
      return new Promise(async (resolve, reject) => {
        try {
          const { themes } = tenantCtx()
          const { themesStorage } = tenantCtx()
          const docRef = doc(themes, `${state.current.data.id}/files/${payload.id}`)

          const fileRef = storageRef(
            themesStorage,
            `${state.current.data.id}/${payload.path}/${payload.name}.${payload.type}`,
          )

          await deleteObject(fileRef)

          // Soft delete in the DB
          await updateDoc(docRef, {
            deletedAt: getUnix(),
          })

          resolve(true)
        } catch (error) {
          console.debug(error)

          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong deleting the {title}', {
              title: i18n.t(`file ${payload.name}.${payload.type}`),
            }),
          })

          reject(error)
        }
      })
    },

    /**
     * Deletes an External Resources of the current Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} payload - The payload used to delete the External Resource
     *
     * @returns {Promise}
     */
    async deleteCurrentThemeExternalResource({ state }, payload) {
      return new Promise((resolve, reject) => {
        try {
          const { themes } = tenantCtx()
          const docRef = doc(themes, `${state.current.data.id}/externalResources/${payload.id}`)

          // Soft delete in the DB
          updateDoc(docRef, {
            deletedAt: getUnix(),
          })
            .then(() => {
              resolve(true)
            })
        } catch (error) {
          console.debug(error)

          store.dispatch('notify', {
            variant: 'danger',
            body: i18n.t('Something went wrong deleting the {title}', {
              title: i18n.t(`External Resource ${payload.name}`),
            }),
          })

          reject(error)
        }
      })
    },

    /**
     * Listens/subscribes to Firestore changes of the current Theme.
     *
     * @param {Object} obj
     * @param {Object} obj.state
     * @param {Object} obj.commit
     *
     * @returns {Object}
     */
    async currentThemeListener({ state, commit }) {
      const { themes } = tenantCtx()
      const docRef = doc(themes, state.current.data.id)

      // Now listen to changes
      const unsubscribe = onSnapshot(docRef, doc => {
        commit('SET_CURRENT', doc)
      })

      return unsubscribe
    },

    /**
     * Soft deletes a Theme by the given payload.
     *
     * @param {Object} obj
     * @param {Object} payload - The payload
     *
     * @returns {Promise}
     */
    softDelete({}, payload) {
      return new Promise((resolve, reject) => {
        const { themes } = tenantCtx()
        const docRef = doc(themes, payload.id)

        updateDoc(docRef, {
          deletedAt: getUnix(),
        })
          .then(() => {
            store.dispatch('revisions/create', {
              event: RevisionEventType.DELETE_THEMES,
              id: payload.id,
              newValue: {},
              previousValue: payload,
            })

            resolve(true)
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    /**
     * Sets the thumbnail canvas element.
     *
     * @param {Object} obj.commit
     * @param {Object} element - The thumbnail canvas element
     *
     * @returns {void}
     */
    setThumbnailCanvasElement({ commit }, element) {
      commit('SET_THUMBNAIL_CANVAS_ELEMENT', element)
    },
  },
}
